/**
 * The voiceover transcript data block: create_voiceover emits a
 * `<script type="application/json" data-codeo-transcript-for="<audioId>">`
 * sibling next to each voiceover `<audio>`, carrying `{text, words}` with
 * per-word timings relative to that audio clip. This module owns parsing and
 * reading those blocks; consumers include the lint rules (timing validation)
 * and the visual review tool (narration context per frame).
 */

import { serializeDocument } from './htmlCanonicalize';

export interface TranscriptWord {
    w: string;
    s: number;
    e: number;
}

export interface ParsedTranscript {
    text: string;
    words: TranscriptWord[];
}

/** Parse a `data-codeo-transcript-for` JSON block. Returns the error message on malformed input. */
export function parseTranscript(raw: string): ParsedTranscript | string {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return `not valid JSON (${e instanceof Error ? e.message : String(e)})`;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return 'expected an object with `text` and `words`';
    }
    const obj = parsed as Record<string, unknown>;
    if (typeof obj.text !== 'string') {
        return 'missing string `text` field';
    }
    if (!Array.isArray(obj.words)) {
        return 'missing array `words` field';
    }
    const words: TranscriptWord[] = [];
    for (let i = 0; i < obj.words.length; i++) {
        const wordObj = obj.words[i] as Record<string, unknown>;
        if (!wordObj || typeof wordObj !== 'object') {
            return `words[${i}] is not an object`;
        }
        if (typeof wordObj.w !== 'string') {
            return `words[${i}] missing string \`w\``;
        }
        if (typeof wordObj.s !== 'number' || !Number.isFinite(wordObj.s)) {
            return `words[${i}] missing finite numeric \`s\``;
        }
        if (typeof wordObj.e !== 'number' || !Number.isFinite(wordObj.e)) {
            return `words[${i}] missing finite numeric \`e\``;
        }
        words.push({ w: wordObj.w, s: wordObj.s, e: wordObj.e });
    }
    return { text: obj.text, words };
}

export interface MomentSpan {
    label: string;
    start: number;
    end: number;
}

/**
 * Map a flat word-timing list back onto the per-moment voiceover lines it was
 * built from, so the agent can snap each moment's clip window — and time its
 * element reveals — to the seconds its line is actually spoken. `segments` are
 * the spoken moment lines IN ORDER; their concatenation (single-space joined)
 * must reproduce the transcript that produced `words`. Alignment is positional:
 * the backend tokenizes the transcript on whitespace, so a per-segment
 * whitespace token count partitions `words` exactly.
 *
 * Returns null when the token counts don't line up (punctuation/normalization
 * drift, or an empty transcript) — the caller hands back nothing rather than
 * wrong numbers, and the agent falls back to reading the transcript directly.
 */
export function computeMomentSpans(
    words: TranscriptWord[],
    segments: { label: string; text: string }[],
): MomentSpan[] | null {
    const counts = segments.map((s) => s.text.trim().split(/\s+/).filter(Boolean).length);
    const total = counts.reduce((sum, n) => sum + n, 0);
    if (total === 0 || total !== words.length) return null;
    const spans: MomentSpan[] = [];
    let idx = 0;
    for (let i = 0; i < segments.length; i++) {
        const n = counts[i];
        if (n === 0) continue; // a visual-only line contributed nothing to the transcript
        spans.push({ label: segments[i].label, start: words[idx].s, end: words[idx + n - 1].e });
        idx += n;
    }
    return spans;
}

/** Selector for the transcript block paired to a voiceover audio element. */
export function transcriptScriptSelector(audioId: string): string {
    return `script[type="application/json"][data-codeo-transcript-for="${CSS.escape(audioId)}"]`;
}

// `</script>` inside an inline script terminates the surrounding tag even
// inside a JSON literal. Standard fix: break the closing tag with a backslash
// (JSON.parse reads `\/` back as `/`, so the payload round-trips).
function escapeScriptBody(body: string): string {
    return body.replace(/<\/script/gi, '<\\/script');
}

/**
 * Serialize a transcript payload as the inline block that sits next to its
 * voiceover `<audio>` in the composition. create_voiceover hands this to the
 * agent verbatim; the lint rules and extractVoiceoverWords read it back.
 */
export function buildTranscriptScriptHtml(audioId: string, transcript: ParsedTranscript): string {
    return (
        `<script type="application/json" data-codeo-transcript-for="${audioId}">` +
        `${escapeScriptBody(JSON.stringify(transcript))}</script>`
    );
}

export interface VoiceoverTranscriptEntry {
    audioId: string;
    start: number;
    text: string;
    // Word timings shifted to absolute composition-timeline seconds.
    words: TranscriptWord[];
}

/**
 * One entry per voiceover `<audio>` that has a transcript block, with word
 * timings shifted to absolute composition-timeline seconds. Backs the
 * get_video transcript scope.
 */
export function extractVoiceoverTranscripts(html: string): VoiceoverTranscriptEntry[] {
    if (typeof DOMParser === 'undefined') return [];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const entries: VoiceoverTranscriptEntry[] = [];
    const audios = Array.from(doc.querySelectorAll('audio[data-codeo-asset="voiceover"]'));
    for (const audio of audios) {
        if (!audio.id) continue;
        const script = doc.querySelector(transcriptScriptSelector(audio.id));
        if (!script) continue;
        const parsed = parseTranscript(script.textContent ?? '');
        if (typeof parsed === 'string') continue; // malformed — lint reports it, skip here
        const offset = Number(audio.getAttribute('data-start')) || 0;
        entries.push({
            audioId: audio.id,
            start: offset,
            text: parsed.text,
            words: parsed.words.map((w) => ({ w: w.w, s: w.s + offset, e: w.e + offset })),
        });
    }
    return entries.sort((a, b) => a.start - b.start);
}

const TRANSCRIPT_BLOCK_RE =
    /<script\b[^>]*data-codeo-transcript-for="([^"]+)"[^>]*>[\s\S]*?<\/script>/gi;

/**
 * Replace each transcript block's word-timing JSON with a short placeholder for
 * full-document reads — the block costs ~800 tokens per voiceover and the agent
 * reads it on demand via get_video({scope:'transcript'}) instead.
 * Display-only: never feed the result back into the stored composition.
 */
export function elideTranscriptBlocks(html: string): string {
    return html.replace(
        TRANSCRIPT_BLOCK_RE,
        (_block, audioId: string) =>
            `<script type="application/json" data-codeo-transcript-for="${audioId}">` +
            `/* transcript elided — read it with get_video({scope:'transcript'}) */</script>`,
    );
}

const ANY_TRANSCRIPT_BLOCK_SELECTOR = 'script[type="application/json"][data-codeo-transcript-for]';

/**
 * Re-derive the transcript registry from a composition's baked blocks (used on
 * load — only currentHtml is persisted; the registry is reconstructed from it,
 * mirroring parseAssetDefs).
 */
export function parseTranscriptsFromHtml(html: string): Record<string, ParsedTranscript> {
    if (typeof DOMParser === 'undefined') return {};
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const transcripts: Record<string, ParsedTranscript> = {};
    for (const block of Array.from(doc.querySelectorAll(ANY_TRANSCRIPT_BLOCK_SELECTOR))) {
        const audioId = block.getAttribute('data-codeo-transcript-for');
        if (!audioId) continue;
        const parsed = parseTranscript(block.textContent ?? '');
        if (typeof parsed === 'string') continue; // malformed/elided — drop, lint reports pairing gaps
        transcripts[audioId] = parsed;
    }
    return transcripts;
}

/**
 * Ensure the HTML carries an up-to-date transcript block after each voiceover
 * audio element with a registry entry (and no blocks anywhere else). The
 * system owns these blocks the way it owns the asset-defs style block: the
 * agent never authors them, so whatever a write or user edit carried in —
 * stale content, an elided placeholder, nothing — is stripped and re-baked
 * from the registry. An audio with no registry entry gets no block; a registry
 * entry with no audio bakes nothing (and revives if the audio comes back).
 */
export function withTranscripts(
    html: string,
    transcripts: Record<string, ParsedTranscript>,
): string {
    const hasEntries = Object.keys(transcripts).length > 0;
    // Fast path keeps non-document fixtures and transcript-free compositions
    // byte-identical — a DOM round-trip would reformat them.
    if (!hasEntries && !html.includes('data-codeo-transcript-for')) return html;
    if (typeof DOMParser === 'undefined') return html;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    for (const block of Array.from(doc.querySelectorAll(ANY_TRANSCRIPT_BLOCK_SELECTOR))) {
        block.remove();
    }
    for (const audio of Array.from(doc.querySelectorAll('audio[data-codeo-asset="voiceover"]'))) {
        const transcript = audio.id ? transcripts[audio.id] : undefined;
        if (!transcript) continue;
        const script = doc.createElement('script');
        script.setAttribute('type', 'application/json');
        script.setAttribute('data-codeo-transcript-for', audio.id);
        script.textContent = escapeScriptBody(JSON.stringify(transcript));
        audio.insertAdjacentElement('afterend', script);
    }
    return serializeDocument(doc);
}

/**
 * Collect every voiceover word in the composition with its timing shifted to
 * absolute composition-timeline seconds (transcript word times are relative to
 * their audio clip's data-start).
 */
export function extractVoiceoverWords(html: string): TranscriptWord[] {
    if (typeof DOMParser === 'undefined') return [];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const words: TranscriptWord[] = [];
    const audios = Array.from(doc.querySelectorAll('audio[data-codeo-asset="voiceover"]'));
    for (const audio of audios) {
        if (!audio.id) continue;
        const script = doc.querySelector(transcriptScriptSelector(audio.id));
        if (!script) continue;
        const parsed = parseTranscript(script.textContent ?? '');
        if (typeof parsed === 'string') continue; // malformed — lint reports it, skip here
        const offset = Number(audio.getAttribute('data-start')) || 0;
        for (const word of parsed.words) {
            words.push({ w: word.w, s: word.s + offset, e: word.e + offset });
        }
    }
    return words.sort((a, b) => a.s - b.s);
}
